import React, { useEffect, useRef, useState } from 'react';
import { peep_client } from '~/src/api/peep';
import { ImagePlus } from 'lucide-react';
import { Uuid, UuidString } from '~src/utils/UUID';
import MediaUploadDisplay from './MediaUploadDisplay';
import { produce } from 'immer';
import { MIME_TYPES } from '~src/utils/mimeTypes';
import { useAbort } from '~src/utils/useAbort';

export interface UploadedMedia {
  file: File;
  url: string;
  uuid: UuidString | null;
  local_uuid: UuidString;
  upload_percent: number | undefined;
}

const SUPPORTED_FILE_TYPES = [MIME_TYPES.JPEG, MIME_TYPES.MP4, MIME_TYPES.PNG];

interface MediaUploaderProps {
  media_uploads: UploadedMedia[];
  setMediaUploads: React.Dispatch<React.SetStateAction<UploadedMedia[]>>;
}

export default function MediaUploader({ media_uploads, setMediaUploads }: MediaUploaderProps) {
  const mediaUploadsRef = useRef(media_uploads);
  const abortRef = useAbort();

  // In the below code we us a ref to media_uploads that is updated every time media_uploads
  // is changed. the reason for this is that react is dumb and there is no other way to reference
  // the current state of state in the return of useEffect
  useEffect(() => {
    return () => {
      mediaUploadsRef.current.forEach((mu) => {
        console.log('revoking: ', mu.url);
        URL.revokeObjectURL(mu.url);
      });
    };
  }, []);

  useEffect(() => {
    mediaUploadsRef.current = media_uploads;
  }, [media_uploads]);

  const startMediaUpload = () => {
    var f = document.createElement('input');
    f.style.display = 'none';
    f.type = 'file';
    f.name = 'file';
    f.accept = SUPPORTED_FILE_TYPES.join(', ');
    f.multiple = true;
    f.click();

    const updatePercent = (upload_percent: number | undefined, local_uuid: Uuid) => {
      setMediaUploads(
        produce((previous) => {
          const media_to_update = previous.findIndex((mu) => mu.local_uuid === local_uuid);

          if (media_to_update != -1) {
            previous[media_to_update].upload_percent = upload_percent;
          }
        }),
      );
    };

    const uploadComplete = (id: UuidString, local_uuid: Uuid) => {
      setMediaUploads(
        produce((previous) => {
          const media_to_update = previous.findIndex((mu) => mu.local_uuid === local_uuid);
          if (media_to_update != -1) {
            previous[media_to_update].uuid = id;
          }
        }),
      );
    };

    f.addEventListener('change', () => {
      if (f.files) {
        const files_to_add: Array<UploadedMedia> = [];
        for (let i = 0; i < f.files.length; i++) {
          if (SUPPORTED_FILE_TYPES.includes(f.files[i].type)) {
            const local_uuid = Uuid.new_v4();
            // Using .then() here to allow image uploads to be rendered and uploaded asynchronously
            // (dont want to await the upload of of file 1 and block the start of image 2)
            peep_client.uploads
              .upload_media({ file: f.files[i], local_uuid }, updatePercent, abortRef.current.signal)
              .then((id) => {
                uploadComplete(id, local_uuid);
              });
            files_to_add.push({
              file: f.files[i],
              url: URL.createObjectURL(f.files[i]),
              uuid: null,
              local_uuid,
              upload_percent: 0,
            });
          } else {
            //TODO: Error state/feedback to user
            console.log('unsupported file type selected: ', f.files[i].type);
          }
        }
        setMediaUploads([...media_uploads, ...files_to_add]);
      }
    });
  };

  return (
    <div style={{ width: '90%', display: 'flex', flexDirection: 'column', margin: '1rem 0 0 0' }}>
      <p style={{ margin: '0' }}>Image Upload</p>
      <div
        style={{
          borderRadius: '5px',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: 'var(--global-gray-lighter-3)',
          display: 'flex',
          flexWrap: 'wrap',
          minHeight: '4.25rem',
          justifyContent: media_uploads.length > 0 ? 'start' : 'center',
        }}
      >
        {media_uploads.map((mu, i) => {
          return (
            <MediaUploadDisplay
              display_url={mu.url}
              key={i}
              upload_percent={mu.upload_percent}
              type={mu.file.type}
              completed={mu.uuid != null}
            />
          );
        })}
        <ImagePlus
          height="3.75rem"
          width="3.75rem"
          strokeWidth="1px"
          style={{ margin: 'auto 0' }}
          onClick={() => {
            startMediaUpload();
          }}
        />
      </div>
    </div>
  );
}
